// Normalized app types – keep values lowercase for internal logic / comparisons
export const EXTERNAL_LINK_TYPES = {
    SECTION_LINK: 'section link',
    SSO_LINK_IN_APP: 'sso link in app',
    SSO_LINK: 'sso link name',
    URL_IN_APP: 'url in app',
    SSO_URL: 'sso url',
    URL: 'url',
    PAGE_SECTION: 'page section',
} as const;

export type NormalizedExternalLinkType = (typeof EXTERNAL_LINK_TYPES)[keyof typeof EXTERNAL_LINK_TYPES];

// AEM CF values – match letter case from the content model
export const AEM_EXTERNAL_LINK_TYPES = {
    SECTION_LINK: 'Section link',
    SSO_LINK_IN_APP: 'SSO link in app',
    SSO_LINK: 'SSO link name',
    URL_IN_APP: 'URL in app',
    SSO_URL: 'SSO URL',
    URL: 'URL',
    PAGE_SECTION: 'Page section',
} as const;

export type AemExternalLinkType = (typeof AEM_EXTERNAL_LINK_TYPES)[keyof typeof AEM_EXTERNAL_LINK_TYPES];

const ICC_EXTERNAL_LINK_TYPES = {
    SECTION_LINK: 'section link',
    SSO_LINK_IN_APP: 'sso link in app',
    SSO_LINK: 'sso link name',
    URL_IN_APP: 'url in app',
    SSO_URL: 'sso url',
    URL: 'url',
    PAGE_SECTION: 'page section',
} as const;

const FIREBASE_EXTERNAL_LINK_TYPES = {
    SECTION_LINK: 'section_link',
    SSO_LINK_IN_APP: 'sso_link_in_app',
    SSO_LINK: 'sso_link',
    URL_IN_APP: 'url_in_app',
    SSO_URL: 'sso_url',
    URL: 'url',
    PAGE_SECTION: 'page_section',
    CONTEST_WINNERS: 'contest_winners',
} as const;

type IccExternalLinkType = (typeof ICC_EXTERNAL_LINK_TYPES)[keyof typeof ICC_EXTERNAL_LINK_TYPES];
type FirebaseExternalLinkType = (typeof FIREBASE_EXTERNAL_LINK_TYPES)[keyof typeof FIREBASE_EXTERNAL_LINK_TYPES];

const normalizeActionTypeCode = (linkType: string): string => {
    const firebaseToExternalMapping: Partial<Record<FirebaseExternalLinkType, NormalizedExternalLinkType>> = {
        [FIREBASE_EXTERNAL_LINK_TYPES.SECTION_LINK]: EXTERNAL_LINK_TYPES.SECTION_LINK,
        [FIREBASE_EXTERNAL_LINK_TYPES.SSO_LINK_IN_APP]: EXTERNAL_LINK_TYPES.SSO_LINK_IN_APP,
        [FIREBASE_EXTERNAL_LINK_TYPES.SSO_LINK]: EXTERNAL_LINK_TYPES.SSO_LINK,
        [FIREBASE_EXTERNAL_LINK_TYPES.URL_IN_APP]: EXTERNAL_LINK_TYPES.URL_IN_APP,
        [FIREBASE_EXTERNAL_LINK_TYPES.SSO_URL]: EXTERNAL_LINK_TYPES.SSO_URL,
        [FIREBASE_EXTERNAL_LINK_TYPES.URL]: EXTERNAL_LINK_TYPES.URL,
        [FIREBASE_EXTERNAL_LINK_TYPES.PAGE_SECTION]: EXTERNAL_LINK_TYPES.PAGE_SECTION,
    };

    const iccToExternalMapping: Partial<Record<IccExternalLinkType, NormalizedExternalLinkType>> = {
        [ICC_EXTERNAL_LINK_TYPES.SECTION_LINK]: EXTERNAL_LINK_TYPES.SECTION_LINK,
        [ICC_EXTERNAL_LINK_TYPES.SSO_LINK_IN_APP]: EXTERNAL_LINK_TYPES.SSO_LINK_IN_APP,
        [ICC_EXTERNAL_LINK_TYPES.SSO_LINK]: EXTERNAL_LINK_TYPES.SSO_LINK,
        [ICC_EXTERNAL_LINK_TYPES.URL_IN_APP]: EXTERNAL_LINK_TYPES.URL_IN_APP,
        [ICC_EXTERNAL_LINK_TYPES.SSO_URL]: EXTERNAL_LINK_TYPES.SSO_URL,
        [ICC_EXTERNAL_LINK_TYPES.URL]: EXTERNAL_LINK_TYPES.URL,
        [ICC_EXTERNAL_LINK_TYPES.PAGE_SECTION]: EXTERNAL_LINK_TYPES.PAGE_SECTION,
    };

    return (
        firebaseToExternalMapping[linkType as FirebaseExternalLinkType] ||
        iccToExternalMapping[linkType as IccExternalLinkType] ||
        linkType
    );
};

export const APP_SECTION_TYPES = {
    LEVIA: 'levia',
    FIRMWARE_AVAILABLE: 'fw',
    DETAILS: 'detailed',
    SHOP: 'shop',
} as const;

export type AppSectionType = (typeof APP_SECTION_TYPES)[keyof typeof APP_SECTION_TYPES];

const isTypeEqual = (type: string | undefined | null, typeToCompare: string): boolean =>
    type?.toLowerCase() === typeToCompare;

export const externalLinkTypes = {
    normalizeActionTypeCode,
    isAppSectionDetails: (appSection?: string | null) => isTypeEqual(appSection, APP_SECTION_TYPES.DETAILS),
    isAppSectionShop: (appSection?: string | null) => isTypeEqual(appSection, APP_SECTION_TYPES.SHOP),
    isSectionLink: (type?: string | null) => isTypeEqual(type, EXTERNAL_LINK_TYPES.SECTION_LINK),
    isSsoLink: (type?: string | null) => isTypeEqual(type, EXTERNAL_LINK_TYPES.SSO_LINK),
    isSsoLinkInApp: (type?: string | null) => isTypeEqual(type, EXTERNAL_LINK_TYPES.SSO_LINK_IN_APP),
    isUrlInApp: (type?: string | null) => isTypeEqual(type, EXTERNAL_LINK_TYPES.URL_IN_APP),
    isSsoUrl: (type?: string | null) => isTypeEqual(type, EXTERNAL_LINK_TYPES.SSO_URL),
    isUrl: (type?: string | null) => isTypeEqual(type, EXTERNAL_LINK_TYPES.URL),
    isPageSection: (type?: string | null) => isTypeEqual(type, EXTERNAL_LINK_TYPES.PAGE_SECTION),
};
